import {
  Box,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  Table,
  Text,
} from "@chakra-ui/react";
import type { Product } from "@/shared/core-products";
import { LuX } from "react-icons/lu";

export type ProductState = {
  data: Product;
  amount: number | string;
  missingStock?: number;
  price?: number;
  comment?: string;
};

export interface ProductsTableProps {
  products?: ProductState[];
  onChange: (products: ProductState[]) => void;
  options?: {
    amount?: boolean;
    missingStock?: boolean;
    price?: boolean;
    comment?: boolean;
  };
  showDeleteButton?: boolean;
  emptyText?: string;
}

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
  whiteSpace: "nowrap",
} as const;

const formatCLP = (value: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(value);

export const ProductsTable = ({
  products = [],
  options,
  onChange,
  showDeleteButton = true,
  emptyText = "No hay productos",
}: ProductsTableProps) => {
  const update = (id: string, patch: Partial<ProductState>) =>
    onChange(products.map((p) => (p.data.id === id ? { ...p, ...patch } : p)));

  return (
    <Box borderWidth="1px" borderColor="border" rounded="lg" overflow="hidden">
      <Table.ScrollArea>
        <Table.Root size="sm" variant="line">
          <Table.Header bg="bg.subtle">
            <Table.Row>
              <Table.ColumnHeader {...headerProps}>Código</Table.ColumnHeader>
              <Table.ColumnHeader {...headerProps}>
                Descripción
              </Table.ColumnHeader>
              {options?.amount && (
                <Table.ColumnHeader {...headerProps} textAlign="end">
                  Cantidad
                </Table.ColumnHeader>
              )}
              <Table.ColumnHeader {...headerProps} textAlign="end">
                Stock
              </Table.ColumnHeader>
              <Table.ColumnHeader {...headerProps}>Unidad</Table.ColumnHeader>
              <Table.ColumnHeader {...headerProps}>Bodega</Table.ColumnHeader>
              {options?.missingStock && (
                <Table.ColumnHeader {...headerProps} textAlign="end">
                  Stock faltante
                </Table.ColumnHeader>
              )}
              {options?.price && (
                <Table.ColumnHeader {...headerProps} textAlign="end">
                  Precio unit.
                </Table.ColumnHeader>
              )}
              {options?.comment && (
                <Table.ColumnHeader {...headerProps}>
                  Observación
                </Table.ColumnHeader>
              )}
              {showDeleteButton && <Table.ColumnHeader width="1%" />}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {products.map((product) => {
              const imageUrl = (product.data as Record<string, unknown>)
                .imageUrl as string | undefined;
              const isErpProduct = !product.data.externalProduct;
              const shortfall =
                (Number(product.amount) || 0) -
                (Number(product.data.stock) || 0);
              return (
                <Table.Row key={product.data.id}>
                  <Table.Cell
                    fontWeight="semibold"
                    whiteSpace="nowrap"
                    maxW="140px"
                    truncate
                    title={
                      product.data.externalProduct?.code ||
                      product.data.erpProduct?.erpCode
                    }
                  >
                    {product.data.externalProduct?.code ||
                      product.data.erpProduct?.erpCode}
                  </Table.Cell>
                  <Table.Cell minW="200px" maxW="320px">
                    <HStack gap={3}>
                      {imageUrl && (
                        <a href={imageUrl} target="_blank" rel="noreferrer">
                          <Image
                            src={imageUrl}
                            alt={product.data.description}
                            boxSize="32px"
                            objectFit="cover"
                            rounded="md"
                          />
                        </a>
                      )}
                      <Text>{product.data.description}</Text>
                    </HStack>
                  </Table.Cell>
                  {options?.amount && (
                    <Table.Cell textAlign="end">
                      <Input
                        type="number"
                        size="sm"
                        w="88px"
                        textAlign="end"
                        fontWeight="semibold"
                        aria-label="Cantidad"
                        value={product.amount}
                        borderColor={shortfall > 0 ? "orange.400" : undefined}
                        onChange={(e) =>
                          update(product.data.id, {
                            amount: Number.parseInt(e.target.value) || 0,
                          })
                        }
                      />
                      {shortfall > 0 && (
                        <Text
                          fontSize="xs"
                          color="orange.600"
                          whiteSpace="nowrap"
                          mt={1}
                        >
                          Supera el stock en {shortfall}
                        </Text>
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell textAlign="end">{product.data.stock}</Table.Cell>
                  <Table.Cell>{product.data.unit}</Table.Cell>
                  <Table.Cell whiteSpace="nowrap">
                    {product.data.storage?.name}
                  </Table.Cell>
                  {options?.missingStock && (
                    <Table.Cell textAlign="end">
                      <Input
                        type="number"
                        size="sm"
                        w="80px"
                        textAlign="end"
                        aria-label="Stock faltante"
                        value={product.missingStock || 0}
                        onChange={(e) =>
                          update(product.data.id, {
                            missingStock: Number.parseInt(e.target.value),
                          })
                        }
                      />
                    </Table.Cell>
                  )}
                  {options?.price && (
                    <Table.Cell textAlign="end" whiteSpace="nowrap">
                      {isErpProduct && Number.isInteger(product.price) ? (
                        <InputGroup startElement="$" w="120px">
                          <Input
                            type="number"
                            size="sm"
                            textAlign="end"
                            aria-label="Precio unitario"
                            value={(product.price || 0).toString()}
                            onChange={(e) =>
                              update(product.data.id, {
                                price: Number.parseInt(e.target.value) || 0,
                              })
                            }
                          />
                        </InputGroup>
                      ) : (
                        <Text color="fg.muted">
                          {product.price ? formatCLP(product.price) : "—"}
                        </Text>
                      )}
                    </Table.Cell>
                  )}
                  {options?.comment && (
                    <Table.Cell>
                      <Input
                        size="sm"
                        minW="180px"
                        placeholder="—"
                        aria-label="Observación"
                        value={product.comment || ""}
                        onChange={(e) =>
                          update(product.data.id, { comment: e.target.value })
                        }
                      />
                    </Table.Cell>
                  )}
                  {showDeleteButton && (
                    <Table.Cell textAlign="end">
                      <IconButton
                        aria-label="Quitar producto"
                        size="sm"
                        variant="ghost"
                        color="fg.muted"
                        onClick={() =>
                          onChange(
                            products.filter(
                              (p) => p.data.id !== product.data.id,
                            ),
                          )
                        }
                      >
                        <LuX />
                      </IconButton>
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>

      {products.length === 0 && (
        <Box textAlign="center" py={8} color="fg.muted" fontSize="sm">
          {emptyText}
        </Box>
      )}
    </Box>
  );
};
